import { useEffect, useState } from "react";
import {
  BUILD_COMMIT,
  BUILD_TIME,
  formatCommitSha,
  formatDeployAge,
  formatDeployTimestamp,
} from "@/lib/buildInfo";

/** How often the "3h ago" label is recomputed for a long-lived tab. */
const TICK_MS = 60_000;

interface BuildInfoProps {
  /**
   * Full commit sha; defaults to the one inlined at build time. Pass `null`
   * (not `undefined`, which selects the default) to model a missing value.
   */
  commit?: string | null;
  /** ISO build timestamp; same defaulting rules as `commit`. */
  builtAt?: string | null;
}

/**
 * Footer line identifying the running bundle: the short commit sha and how long
 * ago it was deployed. Renders nothing when neither value was injected, so a
 * build without git metadata degrades to the sidebar as it was before.
 */
export function BuildInfo({
  commit = BUILD_COMMIT,
  builtAt = BUILD_TIME,
}: BuildInfoProps) {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    if (!builtAt) return;
    const id = setInterval(() => setNow(new Date()), TICK_MS);
    return () => clearInterval(id);
  }, [builtAt]);

  const shortSha = formatCommitSha(commit);
  const age = formatDeployAge(builtAt, now);
  const deployedAt = formatDeployTimestamp(builtAt);

  if (!shortSha && !age) return null;

  return (
    <div className="mt-3 flex items-center justify-between gap-2 px-1 text-[11px] text-neutral-400">
      {shortSha && (
        <span className="font-mono" title={`Commit ${commit}`}>
          {shortSha}
        </span>
      )}
      {age && (
        <span title={deployedAt ? `Deployed ${deployedAt}` : undefined}>
          deployed {age}
        </span>
      )}
    </div>
  );
}
